import './buttons.css';
import PropTypes from 'prop-types';
import React, { useContext, useState } from 'react';
import ApplicationContext from '../ApplicationContext';

const ExtraSmallStyle:React.CSSProperties = {
  height: '18px',
  fontSize: '11px',
  padding: '0px 4px 1px 4px',
  fontWeight:'bold'
};

const SmallStyle:React.CSSProperties = {
  height: '20px',
  fontSize: '12px',
  padding: '1px 12px',
};

const RegularStyle:React.CSSProperties = {
  height: '22px',
  fontSize: '12px',
  padding: '2px 10px',
  //lineHeight: '12px'
};

const LargeStyle:React.CSSProperties = {
  height: '32px',
  fontSize: '16px',
  padding: '2px 10px 4px',
};

const ImgStyle:React.CSSProperties = {
  height: '32px',
};

export type IfmsButtonProps = {
  baseClass: string,
  size: 'regular' | 'small' | 'large' | 'extrasmall' | 'imgStyle',
  bold?: boolean,
  style: React.CSSProperties|null,
  iconStyle?: React.CSSProperties|null,
  focusable?: boolean,
  skipTableCheck?: boolean,
  onTabNext?: ((e:React.KeyboardEvent) => boolean)|undefined,
  onTabPrevious?: ((e:React.KeyboardEvent) => boolean)|undefined,
  hoverIcon?: string,
  icon?: string,
  disabledIcon?: string,
  iconHeight?: string,
  preventDuplicateAction?: boolean,
  iconClass?: string,
  // All other props
  [x: string]: any
};

const IfmsButton = React.forwardRef<HTMLButtonElement, IfmsButtonProps>(({
  baseClass,
  size,
  bold = false,
  style,
  iconStyle,
  children,
  skipTableCheck = false,
  focusable = undefined,
  onTabNext = undefined,
  onTabPrevious = undefined,
  hoverIcon = '',
  icon = '',
  disabledIcon = '',
  iconHeight = '20px',
  preventDuplicateAction = undefined,
  iconClass = '',
  ...other
}, ref) => {
  const appContext = useContext(ApplicationContext);
  const [isMouseOver, setIsMouseOver] = useState(false);
  
  let additionalStyle:React.CSSProperties = !!style ? {...style} : {};
  if ( size === 'small') {
    additionalStyle = !!style ? {...SmallStyle, ...style} : SmallStyle;
  } else if ( size === 'extrasmall') {
    additionalStyle = !!style ? {...ExtraSmallStyle, ...style} : ExtraSmallStyle;
  } else if ( size === 'regular') {
    additionalStyle = !!style ? {...RegularStyle, ...style} : RegularStyle;
  } else if ( size === 'large') {
    additionalStyle = !!style ? {...LargeStyle, ...style} : LargeStyle;
  }else if ( size === 'imgStyle') {
    additionalStyle = !!style ? {...ImgStyle, ...style} : ImgStyle;
  }

  let additonalIconStyle:React.CSSProperties = !!iconStyle ? {...iconStyle} : { position: 'relative' };

  if(bold)
    additionalStyle = {...additionalStyle, fontWeight : 'bold'}

  let buttonClassName = baseClass;

  const { onClick, className, disabled, ...remainingProps } = other;

  if (focusable && !disabled) {
    buttonClassName = `${buttonClassName} ${'ifms-focusable-button ifms-tabable ifms-tabable-active'}`;
  }

  if (className) {
    buttonClassName = `${buttonClassName} ${className}`;
  }

  if(appContext.useDarkMode){
    if(buttonClassName.includes('ifms-focusable-button')){
      buttonClassName = buttonClassName.replace('ifms-focusable-button', 'ifms-focusable-button-darkmode')
    }

    if(buttonClassName.includes('ifms-gray-tab-button')){
      buttonClassName = buttonClassName.replace('ifms-gray-tab-button', 'ifms-gray-tab-button-darkmode')
    }
    else if(buttonClassName.includes('ifms-black-inverse-button')){
      buttonClassName = buttonClassName.replace('ifms-black-inverse-button', 'ifms-black-inverse-button-darkmode')
    }
    else if(buttonClassName.includes('ifms-primary-button')){
      buttonClassName = buttonClassName.replace('ifms-primary-button', 'ifms-deepblue-button')
    }
    else if (buttonClassName.includes('ifms-secondary-button')) {
      buttonClassName = buttonClassName.replace('ifms-secondary-button', 'ifms-deepblue-button')
    }
    else if (buttonClassName.includes('ifms-highlight-button-icon')) {
      buttonClassName = buttonClassName.replace('ifms-highlight-button-icon', 'ifms-highlight-button-icon-Darkmode')
    }
    else if (buttonClassName.includes('ifms-highlight-button')) {
      buttonClassName = buttonClassName.replace('ifms-highlight-button', 'ifms-deepblue-button')
    }
  }

  const handleMouseEnter = () => {
    setIsMouseOver(true);
  };

  const handleMouseLeave = () => {
    setIsMouseOver(false);
  };
  
  const renderButtonIcon = () => {
    if (disabledIcon && disabled) {
      return disabledIcon;
    }
    if (hoverIcon && icon) {
      return isMouseOver ? hoverIcon : icon;
    }
    if (icon)
      return icon;
    return undefined;
  };

  function onKeyDown(e: React.KeyboardEvent) {
    const tab = 9;
    const spaceBarKey = 32;
    const enterKey = 13;

    if (e.keyCode === tab) {
      if (e.shiftKey) {
        processTabPrevious(e);
      } else {
        processTabNext(e);
      }
    }
    else if (e.keyCode === spaceBarKey || e.keyCode === enterKey) {
      if (onClick) onClickWrapper(undefined);
      e.preventDefault();
      e.stopPropagation();
    }
  }

  function processTabPrevious(e:React.KeyboardEvent) {
    if (onTabPrevious) {
      const tabProcessed = onTabPrevious(e);
      if (tabProcessed) {
        e.preventDefault();
        return;
      }
    }
  }

  function processTabNext(e:React.KeyboardEvent) {
    if (onTabNext) {
      const tabProcessed = onTabNext(e);
      if (tabProcessed) {
        e.preventDefault();
        return;
      }
    }
  }

  async function onClickWrapper(e: React.MouseEvent<HTMLButtonElement> | undefined) {
    await onClick(e);
    e?.stopPropagation();
    e?.preventDefault();
  }

  return (
    <button
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      disabled={disabled}
      tabIndex={focusable ? undefined : -1}
      ref={ref}
      onKeyDown={onKeyDown}
      className={buttonClassName}
      style={additionalStyle}
      onClick={async (e) => {
        if (onClick) {
          const element = e.currentTarget as HTMLButtonElement;
          if (preventDuplicateAction) element.disabled = true;
          await onClickWrapper(e);
          if (preventDuplicateAction) element.disabled = false;
        }
      }}
      {...remainingProps}
    >
      {renderButtonIcon() && (
        <img
          height={iconHeight}
          style={additonalIconStyle}
          src={renderButtonIcon()}
          alt=''
        /> 
      )}
      {children}
    </button>
  );
});

IfmsButton.defaultProps = {
  skipTableCheck: false,
  bold : false
};

IfmsButton.propTypes = {
  skipTableCheck: PropTypes.bool.isRequired,
  baseClass: PropTypes.string.isRequired,
  size: PropTypes.oneOf(['regular', 'small', 'large', 'extrasmall', 'imgStyle']),
  bold: PropTypes.bool
};

export default React.memo(IfmsButton);

